import * as React from 'react';
import { css, SerializedStyles } from '@emotion/react';
import { AutocompleterValue, Checkbox, Fade, IconButton, SvgIconBaseProps, TypographyBody } from '..';
import { FixMeLater, scrollChildIntoView, TransitionDuration } from '../../utils';
import { useForkRef, usePrevious, useEventCallback } from '../../hooks';
import { useAutocompleterContext } from '../Autocompleter/useAutocompleter';
import { ButtonBase } from '../ButtonBase/ButtonBase';
import { AutocompleterListProps } from './AutocompleterList';
import { emphasiseSearchTerm } from './utils';
import { AutocompleterOptionAction, useHoneycombTheme } from '../../theming';
import { ChevronRightIcon, CrossIcon } from '../../icons';

const STATIC_STYLES: SerializedStyles = css`
    --autocompleter-option-min-height: 56px;
    --autocompleter-option-active-bg: var(--hc-color-system-info-tint-200);
    --autocompleter-option-padding-x: calc(var(--hc-spacing-unit) * 3);
    --autocompleter-option-padding-y: calc(var(--hc-spacing-unit) * 2);

    // root element
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    gap: var(--autocompleter-option-padding-x);
    min-height: var(--autocompleter-option-min-height);

    padding-inline-start: var(--autocompleter-option-padding-x);
    padding-inline-end: var(--autocompleter-option-padding-x);
    padding-top: var(--autocompleter-option-padding-y);
    padding-bottom: var(--autocompleter-option-padding-y);

    &[data-active] {
        background-color: var(--autocompleter-option-active-bg);
    }

    &[data-inset] {
        // double the start padding
        padding-inline-start: calc(var(--autocompleter-option-padding-x) * 2);
    }

    > [data-autocompleter-option-icon] {
        flex-shrink: 0;
    }

    > [data-autocompleter-option-content] {
        flex-grow: 1;

        [data-autocompleter-option-title] {
            [data-emphasise] {
                font-weight: var(--hc-font-weight-bold);
            }
        }
        [data-autocompleter-option-subtitle] {
            [data-emphasise] {
                font-weight: var(--hc-font-weight-semi-bold);
            }
        }
    }

    > [data-autocompleter-option-control] {
        flex-shrink: 0;

        > * {
            display: flex;
        }
    }
`;

export type AutocompleterOptionProps = {
    /**
     * if `true` the option is active.
     * @default: false
     */
    active?: boolean;

    /**
     * The appendix text.
     */
    appendix?: string;

    /**
     * An `AutocompleterOption` can have a nested `AutocompleterList`.
     */
    children?: React.ReactElement<AutocompleterListProps>;

    /**
     * An optional icon component which will be displayed next to the text.
     */
    icon?: React.ReactElement<SvgIconBaseProps>;

    id: string;

    index?: number;

    /**
     * If `true`, the option will be inset.
     */
    inset?: boolean;

    /**
     * The primary line of text.
     */
    title: string;

    /**
     * The secondary line of textt.
     */
    subtitle?: string;

    /**
     * if `true` the option is selected.
     * @default: false
     */
    selected?: boolean;

    /**
     * The duration for the transition, in milliseconds.
     */
    transitionDuration?: TransitionDuration;

    value: AutocompleterValue;

    /**
     * Which components of an option should have the search term emphasised.
     * @default ['title']
     */
    searchTermMatchedTo?: Array<'title' | 'subtitle' | 'appendix'>;

    /**
     * Determines which action control which will appear on an multi-select option.
     * @default default
     */
    action?: AutocompleterOptionAction;
};

export const AutocompleterOption: React.ForwardRefExoticComponent<
    AutocompleterOptionProps & React.RefAttributes<HTMLElement>
> = React.forwardRef(function AutocompleterOption(
    props: AutocompleterOptionProps,
    ref: React.ForwardedRef<FixMeLater>
): React.JSX.Element | null {
    const { theme } = useHoneycombTheme();
    const { transitions } = theme;

    const {
        active = false,
        appendix,
        children,
        icon,
        id,
        index,
        inset,
        title,
        selected = false,
        subtitle,
        transitionDuration = {
            enter: transitions.duration.shorter,
            exit: transitions.duration.shortest,
        },
        value,
        searchTermMatchedTo = ['title'],
        action = 'default',
        ...other
    } = props;

    const {
        addSelection,
        multiSelect,
        removeSelection,
        searchTerm,
        setActiveOptionId,
        setToggleOptionAtIndex,
        toggleOptionAtIndex,
        scrollContext,
    } = useAutocompleterContext();

    const [open, setOpen] = React.useState(true);
    const prevActive = usePrevious(active);
    const [exited, setExited] = React.useState(false);

    const nodeRef = React.useRef<HTMLLIElement | null>(null);
    const handleRef = useForkRef(ref, nodeRef);

    const handleClick = useEventCallback(() => {
        if (index !== undefined) {
            setToggleOptionAtIndex?.(index);
        }
    });

    const handleExited = (): void => {
        setExited(true);
        setOpen(false);
    };

    React.useEffect(() => {
        if (!prevActive && active) {
            // If we have a scroll context then ensure that the active element is in view.
            if (scrollContext && nodeRef.current) {
                scrollChildIntoView(nodeRef.current, scrollContext);
            }

            // We need to let the provider know this option's id so that we are able to set 'aria-activedescendant' on the owning `AutocompleterField`.
            setActiveOptionId?.(id);
        }
    }, [active, prevActive, setActiveOptionId, id, scrollContext]);

    React.useEffect(() => {
        if (toggleOptionAtIndex === index) {
            if (selected && (multiSelect || action === 'remove')) {
                removeSelection?.(value, title);
            } else {
                addSelection?.(value, title);
            }
        }
    }, [action, addSelection, index, multiSelect, removeSelection, selected, title, toggleOptionAtIndex, value]);

    const getActionControl = React.useCallback(() => {
        if (action === 'checkbox' || (multiSelect && action === 'default')) {
            return (
                <Checkbox
                    size="s"
                    id={`checkbox-${id}`}
                    checked={selected}
                    tabIndex={-1}
                    data-autocompleter-option-checkbox={true}
                />
            );
        }

        if (action === 'single-select') {
            return <ChevronRightIcon size="s" color="primary.base" data-autocompleter-option-select-button={true} />;
        }

        if (action === 'remove') {
            return (
                <IconButton size="xs" color="primary" data-autocompleter-option-remove-button={true}>
                    <CrossIcon />
                </IconButton>
            );
        }

        return null;
    }, [action, multiSelect, id, selected]);

    const actionControl = getActionControl();

    const component = (
        <li ref={handleRef}>
            <ButtonBase
                css={STATIC_STYLES}
                tabIndex={-1}
                aria-selected={selected}
                component="div"
                role="option"
                onClick={handleClick}
                data-autocompleter-option={true}
                data-active={active || undefined}
                data-inset={inset || undefined}
                data-index={index}
                {...other}
            >
                {icon &&
                    React.cloneElement(icon, {
                        size: 'xs',
                        color: 'primary',
                        'data-autocompleter-option-icon': true,
                        ...icon.props,
                    })}
                <div data-autocompleter-option-content={true}>
                    <TypographyBody component="div" weight="semiBold" size="s" data-autocompleter-option-title={true}>
                        {searchTerm && searchTermMatchedTo.includes('title')
                            ? emphasiseSearchTerm(title, searchTerm)
                            : title}
                    </TypographyBody>
                    {subtitle && (
                        <TypographyBody
                            component="div"
                            size="xs"
                            color="text.light"
                            data-autocompleter-option-subtitle={true}
                        >
                            {searchTerm && searchTermMatchedTo.includes('subtitle')
                                ? emphasiseSearchTerm(subtitle, searchTerm)
                                : subtitle}
                        </TypographyBody>
                    )}
                </div>
                {appendix && (
                    <TypographyBody component="div" size="s" data-autocompleter-option-appendix={true}>
                        {searchTerm && searchTermMatchedTo.includes('appendix')
                            ? emphasiseSearchTerm(appendix, searchTerm)
                            : appendix}
                    </TypographyBody>
                )}
                {actionControl && <div data-autocompleter-option-control={true}>{actionControl}</div>}
            </ButtonBase>
            {children}
        </li>
    );

    if (multiSelect) {
        return open || !exited ? (
            <Fade appear={true} in={open} timeout={transitionDuration} onExited={handleExited}>
                {component}
            </Fade>
        ) : null;
    }

    return component;
});
